import { AppDate } from '../envTypes';

import {
  ModelBase,
  ShowCustomization,
  ShowPermission,
  ShippingMode,
} from './base';
import { Profile } from './profile';
import { Address, PickupAddress } from './address';
import { StoreFront } from './store';
import { PopStoreUserAccessLevel } from './creator-subscription';
import { GiftingStatus, Order } from './order';
import { Listing } from './listing';
import { UpcomingShow } from './upcoming-show';
import { PausedBy, ReportReason, VisibilityType } from './shared';
import { ModerationAuditLog, ModerationStatus } from './moderation';

export enum TimerStatus {
  RUNNING = 'RUNNING',
  TERMINATED = 'TERMINATED',
}

export enum TimerType {
  DATE_TIME = 'DATE_TIME',
}

// liveStreams/{id}/timers
// upcomingShows/{id}/timers
export type ShowTimer = DateTimeTimer;
export interface DateTimeTimer extends ModelBase {
  type: TimerType.DATE_TIME;
  status: TimerStatus;
  name: string;
  // the timer will stop at
  maxDate: AppDate;
  // the timer removed at.
  terminatedAt?: AppDate;
}

export interface LiveStreamUser {
  userId: string;
  profile: Profile;
  device?: string;
  lastConnect?: AppDate;
  status: LiveStreamStatus;
}

export interface LiveStreamStatus {
  initialized: boolean;
  available: boolean;
  finished: boolean;
  aborted?: boolean;
}

export enum SoldOutAction {
  MoveToBack = 'MoveToBack',
  RemoveFromShow = 'RemoveFromShow',
}

export enum TwoWayStreamStatus {
  CREATED = 'CREATED',
  CONNECTED = 'CONNECTED',
  ENDED = 'ENDED',
}

// liveStreams/liveStreamId/twoWayStreamRecords/{docId}
export interface TwoWayStreamRecord extends ModelBase {
  viewer: Profile & { nftImageUrl?: string };
  viewerId: string;
  status: TwoWayStreamStatus;
  connectedAt?: AppDate;
  endedAt?: AppDate;
}

export enum AgoraCloudRecordingMode {
  individual = 'individual',
  mix = 'mix',
  web = 'web',
}

export enum RecordingResolution {
  R_720X960 = 'R_720X960',
  R_1080X1920 = 'R_1080X1920',
}

export interface AiModerationResult {
  video_path: string;
  total_frames_analyzed: number;
  exposed_frames_count: number;
  processing_time: number;
  all_labels: string[];
  exposed_labels: string[];
  frames_with_kids: number;
  critical_frames: number;
  count_critical_g: number;
  count_critical_a: number;
  count_critical_b: number;
  highest_confidence_fg: number;
}

export interface LiveStream {
  badges?: string[];
  primary: LiveStreamUser;
  secondary?: LiveStreamUser;
  createdAt?: AppDate;
  disconnectedAt?: AppDate;
  title: string;
  /**
   * @deprecated no longer need limit number of people in livestream since 1/29/2022
   */
  mode: 'normal' | 'cap';
  background?: string;
  preview?: string;
  upcomingShow?: UpcomingShow;
  upcomingShowId?: string;
  /**
   * @deprecated replaced by `permission` since 4/9/2021
   */
  visibility?: VisibilityType;
  permission?: ShowPermission;
  hasListing?: boolean;
  hasOrder?: boolean;
  isNftGated?: boolean;
  isPatreonGated?: boolean;
  nftContracts?: string[];
  patreonIdentifier?: string;
  // @deprecated replaced by `cloudRecording` since 9/2022
  recording?: {
    status: 'unknown' | 'recording' | 'recorded' | 'error';
    videos?: {
      url: string;
      size: number;
    }[];
  };
  cloudRecording?: Record<
    RecordingResolution,
    {
      status: 'unknown' | 'recording' | 'recorded' | 'error';
      m3u8Url?: string;
      bitrate?: number;
    }
  >;
  masterPlaylist?: string;
  eventId?: string;
  eventName?: string;
  userGroupIds?: string[];
  eventTemplateId?: string;

  visibilityOfEventShowOnDiscovery?: boolean;
  versionTag?: string;
  wrapUpEmailSentAt?: AppDate;
  shippingMode?: ShippingMode[];
  pickupAddress?: { addressId: string } & PickupAddress;
  isDebutShow?: boolean;

  streamPausedText?: string;
  soldOutAction?: SoldOutAction;
  pausedBy?: PausedBy;

  visibilityGroupIds?: string[];

  showCustomization?: ShowCustomization;
  startedAt?: AppDate;
  lastReplayedAt?: AppDate;
  upvoteCount?: number;

  moderationStatus?: ModerationStatus;
  moderatedBy?: 'AI' | 'User';
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: string;
  moderationFlag?: string;
  moderationAdditionalMsg?: string;
  adminModerationReason?: string;
  aiStorage?: {
    json_s3_file_key?: string;
    expected_processed_time?: number;
    status?: string;
    error?: string;
    submittedAt?: AppDate;
  };
  // @deprecated, use aiModerationResponse
  aiModerationResult?: AiModerationResult;
  aiModerationResponse?: {
    aiVerdict?: string;
    isContentModerated?: boolean;
    message?: string;
    processedTransportStreamCount?: number;
    auditLogs?: ModerationAuditLog[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiModerationLastError?: any;
  accessLevel?: PopStoreUserAccessLevel;
  tipping?: UpcomingShow['tipping'] & {
    receivedAmount?: number;
  };
}

export interface LiveStreamPurchase {
  seller: Profile;
  sellerId: string;
  buyer: Profile;
  buyerId: string;
  isFirstPurchaseFromSeller?: boolean;
  listingId?: string;
  listing?: Listing;
  createdAt: AppDate;
}

// liveStreams/{liveStreamId}/gifts/{giftId}
export interface Gift {
  fromUser: Profile;
  fromUserId: string;
  toUser: Profile;
  toUserId: string;
  listingId: string;
  listing: Listing;
  createdAt: AppDate;
  status: GiftingStatus;
  orderId?: string;
}

export enum SendItemStatus {
  PROCESSING = 'PROCESSING',
  CANCELED = 'CANCELED',
  CONFIRMED = 'CONFIRMED',
  REJECTED = ' REJECTED',
}

export enum SendItemType {
  normal = 'normal',
  liveStreamAuction = 'liveStreamAuction',
  giveaway = 'giveaway',
}

export interface BaseSendItem {
  fromUser: Profile & { nftImageUrl?: string };
  fromUserId: string;
  toUser: Profile & { nftImageUrl?: string };
  toUserId: string;
  listingId: string;
  listing: Listing;
  createdAt: AppDate;
  variantId?: string;
  price?: number;
  updatedAt?: AppDate;
  status: SendItemStatus;
  needShippingFee: boolean;
  quantity: number;
  /** deprecated replaced by new union type since 06/24/22 */
  liveStreamAuction?: {
    liveStreamId: string;
    auctionId: string;
  };
}

export enum SendItemShowSource {
  upcomingShow = 'upcomingShow',
  liveStream = 'liveStream',
}

export interface NormalSendItem extends BaseSendItem {
  type: SendItemType.normal;
  liveStreamId: string;
}

export interface LiveStreamAuctionSendItem extends BaseSendItem {
  type: SendItemType.liveStreamAuction;
  liveStreamAuction: {
    liveStreamId: string;
    auctionId: string;
  };
}

export interface GiveawaySendItem extends BaseSendItem {
  type: SendItemType.giveaway;
  giveawayItem: {
    giveawayItemId: string;
    source: GiveawaySource;
  };
}

export type SendItem =
  | NormalSendItem
  | LiveStreamAuctionSendItem
  | GiveawaySendItem;

export enum GiveawayPrerequisiteEnum {
  IsFollower = 'IsFollower',
  IsCustomer = 'isCustomer',
}

export enum GiveawayCriterionEnum {
  CommentToEnter = 'CommentToEnter',
  ShareToEnter = 'ShareToEnter',
}

export enum GiveawayItemStatus {
  created = 'created',
  sellerPickedWinner = 'sellerPickedWinner',
  winnerDeclined = 'winnerDeclined',
  canceled = 'canceled',
  finished = 'finished',
}

export enum GiveawayItemSourceType {
  upcomingShow = 'upcomingShow',
  liveStream = 'liveStream',
}

export type GiveawayLiveStreamSource = {
  type: GiveawayItemSourceType.liveStream;
  liveStreamId: string;
};

export type GiveawayUpcomingShowSource = {
  type: GiveawayItemSourceType.upcomingShow;
  upcomingShowId: string;
};

export type GiveawaySource =
  | GiveawayLiveStreamSource
  | GiveawayUpcomingShowSource;

export type GiveawayItem = {
  seller: Profile & { nftImageUrl?: string };
  sellerId: string;
  listingId: string;
  listing: Listing;
  variantId?: string;
  price?: number;
  discountPrice?: number;

  status: GiveawayItemStatus;
  quantity: number;

  totalEntrants: number;
  prerequisites?: GiveawayPrerequisiteEnum[];
  criteria?: GiveawayCriterionEnum[];
  entryCloseAt?: AppDate;

  createdAt: AppDate;
  updatedAt?: AppDate;

  source: GiveawaySource;

  coverImages?: string[];
  title?: string;
  description?: string;
};

export interface GiveawayEntrant {
  profile: Profile & { nftImageUrl?: string };
  entryPoint: number;
  entryCriteria?: GiveawayCriterionEnum[];
  entryPrerequisites?: GiveawayPrerequisiteEnum[];
  joinedAt: AppDate;

  source: GiveawaySource;

  isWinner?: boolean;
  winningAt?: AppDate;
  winnerActionAt?: AppDate;
  status?: 'processing' | 'confirm' | 'reject';
}

export interface GiveawayWinner extends GiveawayEntrant {
  isWinner: true;
  winningAt: AppDate;
  winnerActionAt: AppDate;
  status: 'processing' | 'confirm' | 'reject';
}

export interface SellerPasscode {
  passcode: string;
  createdAt: AppDate;
}

export interface PopDummyModel {
  status: 'notused' | 'notused2';
}

export interface SiteSeller {
  firstName: string;
  lastName: string;
  email: string;
  socialMediaUrl?: string;
  storeUrl?: string;
  phoneNumber?: string;
  note?: string;
  createdAt: AppDate;
}

export interface LiveStreamStateCustomer {
  userId: string;
  orderId: string;
  purchasedAt: AppDate;
  firstOrder: boolean;
  firstOrderPurchasedAt: AppDate;
}

export interface LiveStreamState {
  updatedAt?: AppDate;
  gmv?: number;
  gtv?: number;
  apv?: number;
  aov?: number;
  /** @TJS-type integer */
  viewer: number;
  /** @TJS-type integer */
  follower?: number;
  /** @TJS-type integer */
  like?: number;
  /** @TJS-type integer */
  customer?: number;
  /** @TJS-type integer */
  uniqViewer: number;
  uniqViewerInvitedByThisSeller: number;
  /** @TJS-type integer */
  favorite: number;
  /** @TJS-type integer */
  uniqFavorite: number;
  /** @TJS-type integer */
  comment: number;
  /** @TJS-type integer */
  uniqComment: number;
  /** @TJS-type integer */
  uniqEngaged: number;
  engagedRate: number;
  /** @TJS-type integer */
  share: number;
  revenue: number;
  /** @TJS-type integer */
  uniqCustomer: number;
  /** @TJS-type integer */
  returnCustomer: number;
  /** @TJS-type integer */
  order: number;
  duration: number;
  viewedDuration: number;
  liveStream: LiveStream;
  shippingFee: number;
  shippingFeeAdjusted: number;
  shippingLabelCount: number;
  orderCount: number;
  masterOrder: number;
  itemCount: number;
  orderSale: number;
  orderRefund: number;
  orderTotal: number;
  buyerPaidAmount: number;
  sellerGotAmount: number;
  salesTaxAmount: number;
  customers: LiveStreamStateCustomer[];
  newCustomerCount?: number;
  newHostCustomerCount?: number;
  viewerIds: string[];
  viewerCount: number;
  newViewerCount: number;
  newHostViewerCount: number;
  firstViewAndPurchasedCount: number;
  engagementCount: number;
  total?: number;
  mainOrderCount?: number;
  watchedSecond?: number;
  averageWatchedSecond?: number;
  commissionFee?: number;
  gatewayProcessingFee?: number;
  netRevenue?: number;
  taxAmount?: number;
  canceledPurchaseCount?: number;
  auctionCount?: number;
}

export interface PublicLiveStreamState {
  updatedAt?: AppDate;
  uniqViewer: number;
  /** @TJS-type integer */
  activeViewer?: number;
  /** @TJS-type integer */
  favorite?: number;
  isLive: boolean;
}

export interface Customer {
  profile: Profile;
  address?: Address;
  order?: number;
  /**
   * @deprecated
   */
  revenue?: number;
  totalPaid?: number;
  duration?: number;
  show?: number;
  like?: number;
  share?: number;
  comment?: number;
  firstOrder?: Order;
  firstOrderId?: string;
  secondOrder?: Order;
  secondOrderId?: string;
  lastOrder?: Order;
  lastOrderId?: string;
  hasPurchased: boolean;
  showWatched?: number;
  showPurchased?: number;
  orderTotal?: number;
  buyerPaidAmount?: number;
  itemPurchased?: number;
  orderCount?: number;
  salesTaxAmount?: number;
  shippingFee?: number;
}

export interface FeaturedStore {
  userId: string;
  /**
   * @deprecated replaced by `sortedAt` since 11/30/20
   */
  position: number;
  sortedAt?: AppDate;
  store: StoreFront;
}

export interface BlockedUser {
  reason: ReportReason | 'seller_block' | 'user_block';
  profile: Profile;
  blockedAt?: AppDate;
}

export interface Report {
  targetId: string;
  targetProfile: Profile;
  reporterId: string;
  reporterProfile: Profile;
  reportFrom: {
    documentId: string;
    documentType:
      | 'LiveStream'
      | 'UpcomingShow'
      | 'Listing'
      | 'Profile'
      | 'VideoClip'
      | 'OrderReview';
  };
  reportContent?: {
    contentType:
      | 'Comment'
      | 'LiveStream'
      | 'UpcomingShow'
      | 'Listing'
      | 'Profile'
      | 'VideoClip'
      | 'OrderReview';
    documentId: string;
    content?: string;
  };
  reason: ReportReason;
  createdAt: AppDate;
}

export interface Hashtag {
  tag: string;
  count: number;
  createdAt: AppDate;
  enabled: boolean;
}

export interface OrderSummary {
  liveStreamId: string;
  userId: string;
  user: Profile;
  status: 'pending' | 'success' | 'failed' | 'closed';
  downloadUrl?: string;
  liveStreamCreatedAt: AppDate;
  createdAt: AppDate;
  updatedAt: AppDate;
  orderCount?: number;
  shippingLabelCount?: number;
  packingSlipCount?: number;
}

export interface TestAccount {
  name: string;
  userId: string;
}

export type SellerInviteStatus = 'initial' | 'processing' | 'finished';

export interface SellerInvite extends ModelBase {
  presenteeUserProfile: {
    firstName: string;
    lastName: string;
    email: string;
    storeFrontName: string;
    phone?: string;
  };
  referrerUserId: string;
  referrerUserStoreName: string;
  status: SellerInviteStatus;
}

export interface BonusTransaction {
  userId: string;
  userProfile: Profile;
  type: 'newClient' | 'shopping';
  amount: number;
  balance: number;
  createdAt: AppDate;
  orderId?: string;
  referrerUserId?: string;
  referrerUserProfile?: Profile;
}

// export interface ReferralTransaction {
//   userId: string;
//   profile: Profile;
//   type: 'signup' | 'purchase';
//   amount: number;
//   referralBalance?: number;
//   createdAt: AppDate;
//   referrerUserId: string;
//   referrerUserProfile: Profile;
// }

export type LiveStreamFav = {
  /** @deprecated replaced by `timestamps` since version 3.42 */
  timestamp: AppDate;
  /** app will collect live at certain interval and only fire one document */
  timestamps?: AppDate[];
  userId: string;
};

export interface LiveStreamViewerChart {
  date: string;
  dateSeconds: number;
  orderCount: number;
  show: number;
  purchase: number;
  engagement: number;
  viewTime: number;
}

export interface LiveStreamViewer {
  refererUser: Profile;
  referrerStore: StoreFront;
  userId: string;
  user: Profile;
  liveStreamId: string;
  liveStream: LiveStream;
  createdAt: AppDate;
  startedAt: AppDate;
  stoppedAt?: AppDate;
  firstView?: boolean;
  firstHostShow?: boolean;
  orders?: { orderId: string; order: Order }[];
  hasOrder?: boolean;
  firstHostCustomer?: boolean;
  watchMinute?: number;
  liveStreamWatched?: number;
  state: {
    like: number;
    share: number;
    comment: number;
    orderCount: number;
    orderTotal: number;
    buyerPaidAmount: number;
    sellerGotAmount: number;
    engagementCount: number;
    isEngagement?: boolean;
  };
}

export enum LiveStreamExperienceRate {
  Beginner = 'BEGINNER',
  Intermediate = 'INTERMEDIATE',
  Pro = 'PRO',
}

export enum LiveStreamActionType {
  Auction = 'AUCTION',
}

export enum LiveStreamAuctionStatus {
  Ongoing = 'ONGOING',
  Closed = 'CLOSED',
  Canceled = 'CANCELED',
}

export interface LiveStreamAction extends ModelBase {
  type: LiveStreamActionType;
}

export interface LiveStreamAuctionBid extends ModelBase {
  userId: string;
  user: Profile;
  isUserBlocked: boolean;
  isValid: boolean;
  bidPrice: number;
  nftImageUrl?: string;
}

export enum LiveStreamAuctionExtendSendItemStatus {
  NONE = 'NONE',
}

export type LiveStreamAuctionSendItemStatus =
  | LiveStreamAuctionExtendSendItemStatus
  | SendItemStatus;

export interface LiveStreamAuction extends LiveStreamAction {
  type: LiveStreamActionType.Auction;
  listingId: string;
  variantId?: string;
  listing: Listing;
  startingPrice: number;
  status: LiveStreamAuctionStatus;
  sendItemStatus: LiveStreamAuctionSendItemStatus;
  winnerBidId?: string;
  winnerBid?: LiveStreamAuctionBid;
  sendItemId?: string;
  timer?: {
    startAt: AppDate;
    durationSeconds: number;
    countdownSeconds: number;
  };
}

export interface LiveStreamActionView extends LiveStreamAction {
  auctionId?: string;
  auction?: LiveStreamAuction & {
    bidCount: number;
    currentBidId?: string;
    currentBid?: LiveStreamAuctionBid;
  };
}

export interface LiveStreamViewerProfile extends Profile {
  isWatching?: boolean;
  startedAt?: AppDate;
  stoppedAt?: AppDate;
  deviceType?: string;
  deviceId?: string;
  nftImageUrl?: string;
  userGroupIds?: string[];
}

export enum ReminderType {
  GiftRecipientAddressRequired = 'GIFT_RECIPIENT_ADDRESS_REQUIRED',
  GiftCreated = 'GIFT_CREATED',
  GiftRejected = 'GIFT_REJECTED',
}

export interface GiftRecipientAddressRequiredReminder {
  type: ReminderType.GiftRecipientAddressRequired;
}

export interface GiftCreatedReminder {
  type: ReminderType.GiftCreated;
  orderInfo: {
    senderId: string;
    liveStreamId?: string | null;
    sender: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    recipientId: string;
    recipient: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    sellerId: string;
    seller: {
      firstName: string;
      lastName: string;
      avatar?: string;
    };
    shippingAddress: Address;
    listingSnapshot: Listing;
    listingId: string;
    variantId?: string | null;
    quantity: number;
    createdAt: AppDate;
    orderId: string;
  };
}

export interface GiftRejectedReminder {
  type: ReminderType.GiftRejected;
  orderInfo: {
    senderId: string;
    liveStreamId?: string | null;
    sender: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    recipientId: string;
    recipient: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    sellerId: string;
    seller: {
      firstName: string;
      lastName: string;
      avatar?: string;
    };
    shippingAddress: Address;
    listingSnapshot: Listing;
    listingId: string;
    variantId?: string | null;
    quantity: number;
    createdAt: AppDate;
    orderId: string;
  };
}

// privateProfiles/{uid}/reminders/{reminderId}
export type Reminder = (
  | GiftRecipientAddressRequiredReminder
  | GiftCreatedReminder
  | GiftRejectedReminder
) &
  ModelBase & { processed: boolean };
